import unescape from 'lodash/unescape';
import first from 'lodash/first';
import pick from 'lodash/pick';
import get from 'lodash/get';
import SynthesisPlannerService from '@Services/SynthesisPlanner';
import { SearchTypes, RxSocketUrls } from '@Constants/CommonConstants';
import { RESULTS_TEXTS } from '@Constants/Results';
import Breadcrumb from '@Services/Breadcrumb';
import searchStorage from '@Services/SearchStorage';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import * as qbHelpersService from '@Services/QBHelpers';
import * as Results from '@Services/Results';
import ResultsInfo from '@Services/ResultsInfo';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';

/**
 * @ngdoc method
 * @methodOf app.results:ReactReactionDetailsHelper
 * @name goToContext
 * @param {String} context
 * @param {Object} item
 * @param {Object} requestParams
 * @description context switching
 */
function goToContext(context, item, requestParams = {}) {
  if (typeof item === 'string') {
    // eslint-disable-next-line no-param-reassign
    item = JSON.parse(unescape(item));
  }

  const searchId = searchStorage.uniqId();
  const id = first(item.keys) || item.id || qbHelpersService.getElementId(context, item);
  const databaseId = item.databaseId || requestParams.databaseId;

  const handleResult = (data, subscription) => {
    const searchContextId =
      context === 'reactions'
        ? 'CROSS_LINKING'
        : `${{ tgitems: 'TARGET', substances: 'SUBSTANCE', citations: 'DOCUMENT' }[context]}_SEARCH`;

    if (!get(data, 'payload.resultName')) {
      return;
    }

    // No results found
    if (!get(data, 'payload.resultSize')) {
      subscription.unsubscribe();
      NotifyDialogsService.openModalWindow({
        text: RESULTS_TEXTS.zeroGoToContextResultsText,
        confirmText: RESULTS_TEXTS.zeroGoToContextResultsConfirmText,
        type: 'info',
        trackingOptions: {
          component: 'Results list',
        },
      });
      return;
    }

    searchStorage
      .extendItem(searchId, {
        query: data.payload.queryId,
        searchType: SearchTypes[searchContextId],
      })
      .then(() => {
        const stateParams = {
          pageNumber: 1,
          sort: '',
          ...pick(data.payload, ['context', 'databaseId', 'hitSetKey', 'resultName', 'resultSize', 'searchId', 'queryId']),
        };
        if (requestParams.breadcrumbComment) {
          stateParams.comment = requestParams.breadcrumbComment;
        }
        if (requestParams.breadcrumbHistoryType) {
          stateParams.historyType = requestParams.breadcrumbHistoryType;
        }

        if (context === 'reactions') {
          ResultsInfo.clearResultsInfo();
          Breadcrumb.activateBreadcrumb(stateParams.resultName, stateParams.databaseId);
        }

        Breadcrumb.createBreadcrumb(stateParams, stateParams.resultSize)
          .then(() => {
            stateParams.resetBreadcrumbs = false;
            ResultsDataManage.redirectToResults(stateParams);

            subscription.unsubscribe();
            Results.cancelSearch(searchId);
          })
          .catch(Logger.error);
      })
      .catch(Logger.reject);
  };
  const observable = ResultsDataManage.getContextInfo(context, id, databaseId, searchId, RxSocketUrls.SearchInfo);
  const subscription = observable.subscribe({
    next: (data) => {
      handleResult(data, subscription);
    },
    error: Logger.error,
  });
}

/**
 * @ngdoc method
 * @methodOf app.results:ReactReactionDetailsHelper
 * @name openTitlesAbstractsDialog
 * @param {Object} citation
 * @param {Object} requestParams
 * @returns {Object} promise which will be resolved on modal close
 * @description opens titles/abstracts dialog
 */
function openTitlesAbstractsDialog(citation, requestParams = {}) {
  // SP conditions case, update citation with resultName info
  if (!requestParams.resultName) {
    const updateResults = (data, subscription) => {
      if (data.payload && data.payload.resultName) {
        SynthesisPlannerService.openTitlesAbstractsDialog(
          {
            databaseId: requestParams.databaseId,
            resultName: data.payload.resultName,
            ...citation,
          },
          goToContext
        );
        subscription.unsubscribe();
      }
    };
    const observable = ResultsDataManage.getContextInfo(
      'citations',
      citation.id,
      requestParams.databaseId,
      searchStorage.uniqId(),
      RxSocketUrls.SearchInfo
    );
    const subscription = observable.subscribe({
      next: (data) => {
        updateResults(data, subscription);
      },
      error: Logger.error,
    });
  } else {
    SynthesisPlannerService.openTitlesAbstractsDialog(
      {
        databaseId: requestParams.databaseId,
        resultName: requestParams.resultName,
        ...citation,
      },
      goToContext
    );
  }
}

/**
 * @ngdoc method
 * @methodOf app.results:ReactReactionDetailsHelper
 * @name getLetterTitle
 * @param {Number} index
 * @returns {String}
 * @description get A,B,C,... for multi products
 */
function getLetterTitle(index) {
  const firstLetterCharCode = 65;
  return String.fromCharCode(index + firstLetterCharCode);
}

const ReactReactionDetailsHelper = {
  goToContext,
  openTitlesAbstractsDialog,
  getLetterTitle,
};

export default ReactReactionDetailsHelper;
